import { API } from '@/shared/plugins/Api/API'

// reported/resources answers in one page and never says there is more.
//
// The cap is applied by the OVH reporting API, upstream of billing, on the rows
// of the whole PCI project — billing only narrows them down to one dataplant
// afterwards, in Go. So the number of rows we receive says nothing about whether
// anything was dropped: ask for thirty days and a third of the pods come back,
// quietly. Counting rows to detect it cannot work.
//
// Narrowing the request server-side is not an option either: component_like is
// in the swagger, but sending it answers 500 — billing wraps every reporting
// client error untyped, so whatever the reporting API makes of that parameter
// reaches us as an internal error.
//
// What is left is keeping each request small enough that the cap is never
// reached, by cutting the window into days and asking for one day at a time.
const CHUNK_MS = 24 * 3600 * 1000
// An absurd window widens the slices instead of firing hundreds of requests.
const MAX_CHUNKS = 40

// Format a date (in ms) as an RFC3339 string ending in Z, e.g. 2026-06-11T09:46:10Z.
const toRfc3339 = (ms) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')

/**
 * Cut [start, until] into slices of a day.
 *
 * `start` and `until` filter on the moment a resource started, so the slices
 * partition the rows exactly: each one is counted in the slice it started in,
 * and their union is what a single request would have returned — minus the
 * truncation. A range we cannot read, or one shorter than a slice, stays whole.
 */
function splitRange (start, until) {
  const startMs = Date.parse(start)
  const untilMs = Date.parse(until)
  if (!Number.isFinite(startMs) || !Number.isFinite(untilMs) || untilMs - startMs <= CHUNK_MS) {
    return [{ start, until }]
  }

  const span = untilMs - startMs
  const chunkMs = Math.max(CHUNK_MS, Math.ceil(span / MAX_CHUNKS))
  const chunks = []
  for (let from = startMs; from < untilMs; from += chunkMs) {
    const to = Math.min(from + chunkMs, untilMs)
    chunks.push({ start: toRfc3339(from), until: to === untilMs ? until : toRfc3339(to) })
  }
  return chunks
}

// Billing reporting endpoints (billing/reporting/v2/*).
// Every method takes:
//   - organizationId
//   - projectId: a dataplant id, optional — leave it out to get the whole organization
//   - start / until: the date range (RFC3339), from `start` up to `until`.
class Consumption extends API {
  constructor (id) {
    super(id, true)
  }

  buildParams (organizationId, projectId, start, until) {
    const params = { organization_id: organizationId }
    if (projectId) params.project_id = projectId
    if (start) params.start = start
    if (until) params.until = until
    return params
  }

  // How much data each query/job read (e.g. lakehouse_manager). Used for analytics cost.
  async getScannedData (organizationId, projectId, start, until) {
    return await this.request({
      method: 'GET',
      url: 'billing/reporting/v2/events/scanned_data',
      params: this.buildParams(organizationId, projectId, start, until)
    })
  }

  // How much data is stored (in bytes) over the time range.
  async getStorage (organizationId, projectId, start, until) {
    return await this.request({
      method: 'GET',
      url: 'billing/reporting/v2/events/storage',
      params: this.buildParams(organizationId, projectId, start, until)
    })
  }

  // DPU used, per worker per hour, split between Data Analytics & App.
  // Which one it is comes from the component name
  // (application-services-decimal vs data-processing-engine-decimal).
  // NOTE: the quantity is x10, so divide by 10 to get the real DPU value.
  async getReportedResources (organizationId, projectId, start, until) {
    const chunks = splitRange(start, until)
    const parts = await Promise.all(
      chunks.map(chunk => this.fetchResources(organizationId, projectId, chunk.start, chunk.until))
    )
    if (parts.length === 1) return parts[0]

    // Slices do not overlap, except on the instant they share. Drop what lands
    // in two of them rather than counting that pod twice.
    const byId = new Map()
    parts.flat().forEach(row => {
      byId.set(row.id || `${row.resourceUUID}-${row.startTime}`, row)
    })
    return Array.from(byId.values())
  }

  async fetchResources (organizationId, projectId, start, until) {
    const response = await this.request({
      method: 'GET',
      url: 'billing/reporting/v2/reported/resources',
      params: this.buildParams(organizationId, projectId, start, until)
    })
    return Array.isArray(response) ? response : []
  }

  // Highest DPU reached during the time range. Returns { max_dpu: Number }.
  async getMaxDpu (organizationId, projectId, start, until) {
    return await this.request({
      method: 'GET',
      url: 'billing/reporting/v2/max_dpu_over_time',
      params: this.buildParams(organizationId, projectId, start, until)
    })
  }
}

export default Consumption
export {
  Consumption
}
